import React, { useState } from 'react';
import { Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { services } from '../data/services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';


const Booking: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ScrollToTop />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-24 md:pb-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6 font-['Instrument_Serif']">
            Book Your Appointment
          </h1>
          <div className="w-24 h-1 bg-amber-500 mb-8 mx-auto md:mx-0"></div>
          <p className="text-center  md:text-left text-xl text-gray-700 dark:text-gray-300 max-w-2xl">
            Schedule your next haircut or styling session with our expert barbers. 
            We're here to help you look and feel your best.
          </p>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          {/* Two-Step Selection Grid */}
          <div className="mx-auto">
            <div className="mb-8">
              {!selectedService ? (
                /* Step One: Select Your Service */
                <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center mb-6">
                    <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
                      1
                    </div>
                    <div className="flex items-center">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Select Your Service
                      </h3>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { name: 'Men', desc: 'Professional men\'s haircuts and styling', price: '£15-25' },
                      { name: 'Women', desc: 'Styling and treatments for women', price: '£20-35' },
                      { name: 'Kids', desc: 'Gentle cuts and styling for children', price: '£10-15' }
                    ].map((service) => (
                      <div 
                        key={service.name} 
                        onClick={() => setSelectedService(service.name)}
                        className="p-6 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer transition-colors duration-200 text-center hover:border-amber-500 dark:hover:border-amber-500 hover:bg-orange-50 dark:hover:bg-amber-900/10"
                      >
                        <div className="flex flex-col items-center">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 text-xl mb-2">{service.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {service.desc}
                          </p>
      
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Step Two: Select Your Style */
                <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
                        2
                      </div>
                      <div className="flex items-center">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                          Select Your Style
                        </h3>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedService(null);
                        setSelectedStyle(null);
                      }}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 px-4 py-2 rounded-full transition-colors duration-300 font-light inline-flex items-center justify-center"
                    >
                      ← Back to Services
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {services
                      .filter(service => service.category === selectedService?.toLowerCase())
                      .map((service) => (
                        <div 
                          key={service.id}
                          className={`bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:scale-[1.02] relative ${
                            selectedStyle === service.name ? 'border-2 border-amber-500' : 'border border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          <div className="h-48 w-full overflow-hidden relative">
                            <img 
                              src={service.image} 
                              alt={`${service.name} - ${service.description}`}
                              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                            />
                            {selectedStyle === service.name && (
                              <div className="absolute top-3 right-3 bg-amber-500 text-white rounded-full p-2 shadow-lg">
                                <Check className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="p-6 flex flex-col">
                            <div className="mb-4">
                              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{service.name}</h3>
                              <p className="text-gray-700 dark:text-gray-300 text-lg">{service.price}</p>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 flex-grow">{service.description}</p>
                            
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button 
                                  onClick={() => setSelectedStyle(service.name)}
                                  className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 font-medium py-2 rounded-full transition-all duration-300 mt-auto"
                                >
                                  Book now
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Booking Confirmation</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Please remember the style you have selected and apply in the booking confirmation form.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => window.open(service.bookingLink, '_blank', 'noopener,noreferrer')}>
                                    OK, Continue
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            
            {/* Call to Action */}
            <div className="text-center mt-8">
              {(selectedService || selectedStyle) && (
                <div className="bg-orange-50 dark:bg-amber-900/20 border border-amber-500 dark:border-amber-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-600 mb-3">
                    Your Selection
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedService && (
                      <p className="text-amber-700 dark:text-amber-500">
                        <span className="font-light">Service:</span> {selectedService}
                      </p>
                    )}
                    {selectedStyle && (
                      <p className="text-amber-700 dark:text-amber-500">
                        <span className="font-light">Style:</span> {selectedStyle}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Ready to book? Contact us to schedule your appointment
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+4420 8743 6788" 
                  className="bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 px-4 py-2 rounded-full transition-colors duration-300 font-medium inline-flex items-center justify-center"
                >
                  Call Now
                </a>
                <a 
                  href="mailto:bookings3d@outlook.com" 
                  className="bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 px-4 py-2 rounded-full transition-colors duration-300 font-medium inline-flex items-center justify-center"
                >
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Booking;
